import { forwardRef, type HTMLAttributes } from "react";
import type { DisciplineSlug } from "@jumpbean/shared";

export type LoaderSize = "sm" | "md" | "lg";

export interface LoaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Size of the loader */
  size?: LoaderSize;
  /** Discipline theming */
  discipline?: DisciplineSlug;
  /** Loading message to display */
  message?: string;
}

/**
 * Playful loading spinner with bouncing animation.
 * Features a friendly, child-appropriate loading indicator.
 *
 * @example
 * // Basic loader
 * <Loader />
 *
 * @example
 * // Large loader with message
 * <Loader size="lg" message="Loading your adventure..." />
 *
 * @example
 * // Discipline-themed loader
 * <Loader discipline="math" message="Getting ready..." />
 */
export const Loader = forwardRef<HTMLDivElement, LoaderProps>(
  (
    {
      size = "md",
      discipline,
      message,
      className = "",
      ...props
    },
    ref
  ) => {
    // Size configurations
    const sizeStyles = {
      sm: {
        container: "gap-2",
        dot: "w-3 h-3",
        message: "text-body-md mt-3",
      },
      md: {
        container: "gap-3",
        dot: "w-5 h-5",
        message: "text-body-lg mt-4",
      },
      lg: {
        container: "gap-4",
        dot: "w-7 h-7",
        message: "text-body-xl mt-5",
      },
    };

    // Discipline colors for dots
    const disciplineColors: Record<DisciplineSlug, string[]> = {
      math: ["bg-luna-light", "bg-luna", "bg-magic-400"],
      spelling: ["bg-petal-light", "bg-petal", "bg-meadow-400"],
      patterns: ["bg-prism-red", "bg-prism-yellow", "bg-prism-blue"],
      typing: ["bg-whiskers-light", "bg-whiskers", "bg-sky-600"],
      programming: ["bg-pixel-light", "bg-pixel", "bg-pixel-darker"],
    };

    // Default rainbow colors
    const defaultColors = ["bg-sunshine-400", "bg-fairy-400", "bg-magic-400"];
    const dotColors = discipline ? (disciplineColors[discipline] ?? defaultColors) : defaultColors;

    const currentSize = sizeStyles[size];

    return (
      <div
        ref={ref}
        role="status"
        aria-label={message ?? "Loading"}
        className={`
          flex flex-col items-center justify-center
          ${className}
        `}
        {...props}
      >
        {/* Bouncing dots */}
        <div className={`flex items-end ${currentSize.container}`}>
          {dotColors.map((color, index) => (
            <div
              key={index}
              className={`
                ${currentSize.dot}
                ${color}
                rounded-full
                shadow-md
                animate-[bounce_1s_ease-in-out_infinite]
              `}
              style={{
                animationDelay: `${index * 150}ms`,
              }}
              aria-hidden="true"
            />
          ))}
        </div>

        {/* Loading message */}
        {message && (
          <p
            className={`
              font-body text-neutral-600
              ${currentSize.message}
              animate-pulse
            `}
          >
            {message}
          </p>
        )}
      </div>
    );
  }
);

Loader.displayName = "Loader";

/**
 * Full-page loader overlay
 */
export interface PageLoaderProps extends LoaderProps {
  /** Whether the overlay is visible */
  isVisible?: boolean;
}

export const PageLoader = forwardRef<HTMLDivElement, PageLoaderProps>(
  ({ isVisible = true, ...props }, ref) => {
    if (!isVisible) return null;

    return (
      <div
        ref={ref}
        className="
          fixed inset-0 z-50
          flex items-center justify-center
          bg-cloud/80 backdrop-blur-sm
        "
      >
        <Loader size="lg" {...props} />
      </div>
    );
  }
);

PageLoader.displayName = "PageLoader";

/**
 * Skeleton loading placeholder for content
 */
export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Width of the skeleton */
  width?: string | number;
  /** Height of the skeleton */
  height?: string | number;
  /** Whether it's a circular skeleton */
  circle?: boolean;
}

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      width,
      height = "1rem",
      circle = false,
      className = "",
      style,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={`
          bg-neutral-200
          animate-pulse
          ${circle ? "rounded-full" : "rounded-lg"}
          ${className}
        `}
        style={{
          width: typeof width === "number" ? `${width}px` : width,
          height: typeof height === "number" ? `${height}px` : height,
          ...style,
        }}
        aria-hidden="true"
        {...props}
      />
    );
  }
);

Skeleton.displayName = "Skeleton";

/**
 * Card skeleton for loading states
 */
export const CardSkeleton = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className = "", ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={`
          p-5
          bg-cloud
          rounded-2xl
          border-[3px] border-neutral-200
          shadow-[0_4px_16px_rgba(0,0,0,0.08)]
          ${className}
        `}
        {...props}
      >
        {/* Header skeleton */}
        <div className="flex items-center gap-3 mb-4">
          <Skeleton width={48} height={48} circle />
          <div className="flex-1 space-y-2">
            <Skeleton width="60%" height={20} />
            <Skeleton width="40%" height={14} />
          </div>
        </div>

        {/* Content skeleton */}
        <div className="space-y-3">
          <Skeleton width="100%" height={16} />
          <Skeleton width="85%" height={16} />
          <Skeleton width="70%" height={16} />
        </div>

        {/* Footer skeleton */}
        <div className="flex justify-between items-center mt-5 pt-4 border-t border-neutral-100">
          <Skeleton width={80} height={24} />
          <Skeleton width={100} height={40} />
        </div>
      </div>
    );
  }
);

CardSkeleton.displayName = "CardSkeleton";

export default Loader;
